import { Fragment } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  ArrowUpRight,
  ArrowDownLeft,
  Pencil,
  RotateCcw,
} from "lucide-react";
import type { ERPIntegrationSync } from "@/store/erpIntegrationsStore";
import { getMappedErpProperty } from "@/components/erp/customFieldMappingHelpers";
import { useResolvedSyncConfigSchema } from "@/components/erp/utils";
import { StatusBadge } from "@/components/settings/StatusBadge";
import {
  SummaryRow,
  MappingCountRow,
} from "@/components/settings/MappingCountRow";
import type { JSONSchema7, JSONSchema7Definition } from "json-schema";
import {
  ERPSyncConfigSchema,
  hasAnyPushEntityEnabled,
} from "@forgepoint/shared";

export interface SyncCardProps {
  /** The sync job to render */
  sync: ERPIntegrationSync;
  /** ID of the sync currently being manually pushed */
  manualPushLoadingId: string | null;
  /** ID of the sync currently being manually pulled */
  manualPullLoadingId: string | null;
  /** ID of the sync whose progress is currently being reset */
  resetLoadingId: string | null;
  /** Callback to open the edit sheet for a sync */
  onEdit: (sync: ERPIntegrationSync) => void;
  /** Callback to trigger a manual push */
  onManualPush: (syncId: string) => void;
  /** Callback to trigger a manual pull */
  onManualPull: (syncId: string) => void;
  /** Callback to reset the sync's last synced timestamps */
  onResetLastSyncedAt: (syncId: string) => void;
  /** Whether the current user can edit syncs or trigger manual push/pull */
  canEdit: boolean;
}

/**
 * Renders a single ERP sync job as a read-only card with a status badge,
 * a summary of the current settings, and action buttons. Editing happens
 * inside the slide-over sheet so all changes save in one PATCH.
 * @param props - SyncCardProps
 * @returns Sync card element
 */
export function SyncCard({
  sync,
  manualPushLoadingId,
  manualPullLoadingId,
  resetLoadingId,
  onEdit,
  onManualPush,
  onManualPull,
  onResetLastSyncedAt,
  canEdit,
}: SyncCardProps) {
  const isPushLoading = manualPushLoadingId === sync.id;
  const isPullLoading = manualPullLoadingId === sync.id;
  const isResetting = resetLoadingId === sync.id;
  const isEnabled = sync.status === "ENABLED";
  const providerName = sync.provider?.name ?? sync.erpIntegrationProviderId;
  const connectionName = sync.connection.name ?? "Unnamed connection";
  const { schema: resolvedSchema, canScan } = useResolvedSyncConfigSchema(
    sync.provider?.id ?? null,
    sync.connectionId ?? null,
  );
  const configEntries = sync.integrationConfig
    ? Object.entries(sync.integrationConfig)
        .filter(
          ([key, value]) =>
            key !== "statusMapping" &&
            key !== "stageMapping" &&
            key !== "opportunityCustomFieldMapping" &&
            key !== "opportunityFieldMapping" &&
            value !== null &&
            value !== undefined,
        )
        .sort((a, b) => {
          const order =
            getSchemaPropertyOrder(resolvedSchema) ??
            getSchemaPropertyOrder(sync.provider?.syncConfigSchema) ??
            [];
          const indexA = order.indexOf(a[0]);
          const indexB = order.indexOf(b[0]);
          if (indexA === -1 && indexB === -1) {
            return 0;
          }
          if (indexA === -1) {
            return 1;
          }
          if (indexB === -1) {
            return -1;
          }
          return indexA - indexB;
        })
    : [];
  const stageMapping = sync.integrationConfig?.stageMapping;
  const stageMappingCount =
    typeof stageMapping === "object" && stageMapping !== null
      ? Object.values(stageMapping).filter(
          (v) => typeof v === "string" && v.length > 0,
        ).length
      : 0;
  const opportunityFieldMapping =
    sync.integrationConfig?.opportunityFieldMapping;
  const opportunityFieldMappingCount =
    typeof opportunityFieldMapping === "object" &&
    opportunityFieldMapping !== null
      ? Object.values(opportunityFieldMapping).filter(
          (v) => typeof v === "string" && v.length > 0,
        ).length
      : 0;
  const opportunityCustomFieldMapping =
    sync.integrationConfig?.opportunityCustomFieldMapping;
  const opportunityCustomFieldMappingCount =
    typeof opportunityCustomFieldMapping === "object" &&
    opportunityCustomFieldMapping !== null
      ? Object.values(opportunityCustomFieldMapping).filter(
          (v) => getMappedErpProperty(v) !== "",
        ).length
      : 0;
  const pushConfigResult = ERPSyncConfigSchema.safeParse(
    sync.integrationConfig ?? {},
  );
  const hasPushEnabled = pushConfigResult.success
    ? hasAnyPushEntityEnabled(pushConfigResult.data)
    : false;

  return (
    <Card className="overflow-hidden shadow-sm transition-shadow duration-200 hover:shadow-md">
      <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{providerName}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {connectionName} &middot; {sync.connection.appId}
          </p>
        </div>
        <StatusBadge enabled={isEnabled} />
      </div>

      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 px-4 py-3 text-xs">
        <SummaryRow
          label="Auto-push"
          value={sync.autoPushEnabled ? "Enabled" : "Disabled"}
          dot={sync.autoPushEnabled ? "active" : "muted"}
        />
        {configEntries.map(([key, value]) => (
          <Fragment key={key}>
            <SummaryRow
              label={getConfigLabel(key, resolvedSchema)}
              value={getConfigDisplayValue(key, value, resolvedSchema)}
              dot={
                typeof value === "boolean"
                  ? value
                    ? "active"
                    : "muted"
                  : undefined
              }
            />
          </Fragment>
        ))}
        {stageMapping !== undefined && (
          <MappingCountRow
            label="Opportunity stage mapping"
            count={stageMappingCount}
          />
        )}
        {opportunityFieldMapping !== undefined && (
          <MappingCountRow
            label="Opportunity field mapping"
            count={opportunityFieldMappingCount}
          />
        )}
        {opportunityCustomFieldMapping !== undefined && (
          <MappingCountRow
            label="Opportunity custom field mapping"
            count={opportunityCustomFieldMappingCount}
          />
        )}
      </dl>

      <div className="flex items-center justify-between gap-3 border-t bg-muted/20 px-4 py-3">
        <p className="text-xs text-muted-foreground">
          Updated {formatDate(sync.updatedAt)}
        </p>
        {canEdit && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(sync)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Pencil className="mr-1.5 h-3 w-3" />
              Edit
            </Button>
            <AlertDialog>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={isEnabled || isResetting}
                        >
                          {isResetting ? (
                            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                          ) : (
                            <RotateCcw className="mr-1.5 h-3 w-3" />
                          )}
                          Reset
                        </Button>
                      </AlertDialogTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {isEnabled
                        ? "Disable this sync before resetting"
                        : "Reset last synced timestamps"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset sync progress?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This clears the last synced timestamp for every entity. The
                    next pull will run a full sync. The integration must remain
                    disabled and cannot be currently running.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onResetLastSyncedAt(sync.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Reset sync
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onManualPull(sync.id)}
                      disabled={isPullLoading || !canScan}
                    >
                      {isPullLoading ? (
                        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                      ) : (
                        <ArrowDownLeft className="mr-1.5 h-3 w-3" />
                      )}
                      Pull now
                    </Button>
                  </span>
                </TooltipTrigger>
                {!canScan && (
                  <TooltipContent>
                    <p>Re-authorize this connection to enable syncing</p>
                  </TooltipContent>
                )}
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onManualPush(sync.id)}
                      disabled={isPushLoading || !canScan || !hasPushEnabled}
                    >
                      {isPushLoading ? (
                        <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                      ) : (
                        <ArrowUpRight className="mr-1.5 h-3 w-3" />
                      )}
                      Push now
                    </Button>
                  </span>
                </TooltipTrigger>
                {(!canScan || !hasPushEnabled) && (
                  <TooltipContent>
                    <p>
                      {!canScan
                        ? "Re-authorize this connection to enable syncing"
                        : "No entity is configured to push"}
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Utility Functions ──

/**
 * Formats a date string to a locale-specific string representation.
 * @param dateString - ISO date string to format
 * @returns Formatted date string
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

/**
 * Converts a camelCase config key into a Sentence-case label
 * (e.g. "syncCompanies" → "Sync companies").
 * @param key - The config key
 * @returns The humanized label
 */
function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Returns the property schema for a given config key from the resolved schema,
 * or null when the schema isn't loaded or the property isn't an object schema.
 * @param schema - The resolved schema (or null)
 * @param key - The config property key
 * @returns The property's schema definition, or null
 */
function getPropertySchema(
  schema: JSONSchema7 | null,
  key: string,
): JSONSchema7 | null {
  if (!schema || schema.type !== "object" || !schema.properties) {
    return null;
  }
  const property: JSONSchema7Definition | undefined = schema.properties[key];
  if (!property || typeof property === "boolean") {
    return null;
  }
  return property;
}

const CONFIG_LABEL_OVERRIDES: Record<string, string> = {
  pipelineId: "Pipeline",
  syncPeople: "Pull people",
  syncCompanies: "Pull companies",
  syncOpportunities: "Pull opportunities",
  syncItems: "Pull items",
  syncOperations: "Pull operations",
  syncPriceLists: "Pull price lists",
  syncLocations: "Pull locations",
  syncInventoryLines: "Pull inventory",
  pushCompanies: "Push companies",
  pushPeople: "Push people",
  pushOpportunities: "Push opportunities",
  pushQuotes: "Push quotes",
  overwriteQuoteOnRevision: "Quote revisions overwrite the ERP quote",
};

/**
 * Extracts a `propertyOrder` string array from a schema-like value, if present.
 * ERP sync config schemas include a `propertyOrder` custom JSON Schema keyword
 * that controls display order (same pattern as custom fields schemas).
 * @param schema - A JSON Schema-like value that may contain `propertyOrder`
 * @returns The property order array, or null if not present or invalid
 */
function getSchemaPropertyOrder(schema: unknown): string[] | null {
  if (
    typeof schema !== "object" ||
    schema === null ||
    !("propertyOrder" in schema)
  ) {
    return null;
  }
  const { propertyOrder } = schema;
  if (
    !Array.isArray(propertyOrder) ||
    !propertyOrder.every((item): item is string => typeof item === "string")
  ) {
    return null;
  }
  return propertyOrder;
}

/**
 * Returns the display label for a config row. Prefers a known override so
 * labels stay stable while the resolved schema loads; then the resolved
 * schema's `title`; then a humanized key.
 * @param key - The config property key
 * @param schema - The resolved provider schema, or null when unavailable
 * @returns The label string for the summary row
 */
function getConfigLabel(key: string, schema: JSONSchema7 | null): string {
  if (CONFIG_LABEL_OVERRIDES[key]) {
    return CONFIG_LABEL_OVERRIDES[key];
  }
  const property = getPropertySchema(schema, key);
  if (property?.title) {
    return property.title;
  }
  return humanizeKey(key);
}

/**
 * Returns the display value for a config row. For enum-backed string fields
 * (e.g. `pipelineId`), looks up the matching `enumNames` entry so the user
 * sees the human-readable label rather than the raw id.
 * @param key - The config property key
 * @param value - The stored config value
 * @param schema - The resolved provider schema, or null when unavailable
 * @returns The display string for the summary row
 */
function getConfigDisplayValue(
  key: string,
  value: unknown,
  schema: JSONSchema7 | null,
): string {
  const property = getPropertySchema(schema, key);
  if (property && typeof value === "string" && Array.isArray(property.enum)) {
    const enumNames = (property as { enumNames?: unknown }).enumNames;
    const index = property.enum.indexOf(value);
    if (index >= 0 && Array.isArray(enumNames)) {
      const label = enumNames[index];
      if (typeof label === "string") {
        return label;
      }
    }
  }
  return renderConfigValue(value);
}

/**
 * Renders a config value for the summary row. Booleans are shown as
 * Enabled/Disabled (paired with a status dot); other values use String().
 * @param value - The value from integrationConfig
 * @returns A display string
 */
function renderConfigValue(value: unknown): string {
  if (typeof value === "boolean") {
    return value ? "Enabled" : "Disabled";
  }
  return String(value);
}
